// Home / landing de saands. Objetivo: convertir visitantes en registros.
// Estructura: hero con CTA → prueba de valor → funciones destacadas → cómo
// funciona → prueba social → planes (teaser) → FAQ → CTA de cierre.
// SEO: título + descripción + JSON-LD (Organization + SoftwareApplication).

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../ui/components.js';
import { IMG } from '../media.js';
import {
  Container,
  CtaBanner,
  CtaPrimary,
  CtaSecondary,
  Eyebrow,
  FaqItem,
  FeatureCard,
  SectionHeading,
} from '../components.js';
import { faqJsonLd, organizationJsonLd, softwareJsonLd, useSeo } from '../seo.js';

export function HomeView() {
  const { t } = useTranslation();
  const homeFaqs = [
    { q: t('marketing.home.faq1Q'), a: t('marketing.home.faq1A') },
    { q: t('marketing.home.faq2Q'), a: t('marketing.home.faq2A') },
    { q: t('marketing.home.faq3Q'), a: t('marketing.home.faq3A') },
    { q: t('marketing.home.faq4Q'), a: t('marketing.home.faq4A') },
  ];

  useSeo({
    title: t('marketing.home.seoTitle'),
    description: t('marketing.home.seoDescription'),
    path: '/',
    image: IMG.coloredVinyl,
    keywords: t('marketing.home.seoKeywords', { returnObjects: true }) as string[],
    jsonLd: [organizationJsonLd(), softwareJsonLd(), faqJsonLd(homeFaqs)],
  });

  return (
    <>
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <img
            src={IMG.coloredVinyl}
            alt=""
            aria-hidden
            width={1200}
            height={800}
            className="w-full h-full object-cover opacity-30"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-surface-base/75 via-surface-base/90 to-surface-base" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(56,189,248,0.18),transparent_55%)]" />
        </div>

        <Container className="relative py-space-xl lg:py-24 flex flex-col items-center text-center gap-space-lg">
          <Eyebrow icon="graphic_eq">{t('marketing.home.heroEyebrow')}</Eyebrow>
          <h1 className="font-display-hero text-display-hero-mobile lg:text-display-hero text-on-surface font-bold tracking-tight max-w-4xl text-balance">
            {t('marketing.home.heroTitlePre')}<span className="text-primary">{t('marketing.home.heroTitleHighlight')}</span>
          </h1>
          <p className="font-body-md text-body-lg text-on-surface-variant max-w-2xl leading-relaxed">
            {t('marketing.home.heroSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-space-sm mt-space-xs">
            <CtaPrimary to="/registro">{t('marketing.cta.startFree')}</CtaPrimary>
            <CtaSecondary to="/funciones" icon="play_circle">
              {t('marketing.home.heroHowItWorks')}
            </CtaSecondary>
          </div>
          <span className="font-badge-micro text-badge-micro text-outline uppercase tracking-wider">
            {t('marketing.home.heroNoCard')}
          </span>

          {/* Barra de confianza: prueba social + atributos clave bajo el hero */}
          <div className="mt-space-md w-full max-w-3xl flex flex-col items-center gap-space-sm">
            <p className="font-body-md text-body-md text-on-surface-variant">
              {t('marketing.home.heroTrustText')}
            </p>
            <ul className="flex flex-wrap items-center justify-center gap-x-space-md gap-y-space-xs">
              {[
                { icon: 'sync', label: t('marketing.home.heroTrustDiscogs') },
                { icon: 'smartphone', label: t('marketing.home.heroTrustIphone') },
                { icon: 'lock', label: t('marketing.home.heroTrustData') },
              ].map((item) => (
                <li
                  key={item.icon}
                  className="flex items-center gap-space-xs font-body-md text-body-sm text-on-surface-variant"
                >
                  <Icon name={item.icon} className="text-[18px] text-primary" aria-hidden />
                  {item.label}
                </li>
              ))}
            </ul>
          </div>
        </Container>
      </section>

      {/* ===== EL PROBLEMA: CATALOGAR A MANO AGOTA ===== */}
      <section className="py-space-xl">
        <Container className="flex flex-col items-center gap-space-lg">
          <h2 className="font-headline-lg text-headline-lg-mobile lg:text-headline-lg text-on-surface font-bold tracking-tight max-w-3xl text-center text-balance">
            {t('marketing.home.painTitle')}
          </h2>
          <ul className="w-full max-w-2xl flex flex-col gap-space-sm">
            {[
              { icon: 'keyboard', label: t('marketing.home.pain1') },
              { icon: 'content_copy', label: t('marketing.home.pain2') },
              { icon: 'payments', label: t('marketing.home.pain3') },
              { icon: 'inventory_2', label: t('marketing.home.pain4') },
            ].map((item) => (
              <li
                key={item.icon}
                className="flex items-start gap-space-sm rounded-xl border border-border-subtle bg-surface-deck px-space-md py-space-sm"
              >
                <span className="mt-0.5 w-8 h-8 shrink-0 rounded-full bg-vacuum-glow/10 border border-vacuum-glow/30 text-vacuum-glow flex items-center justify-center">
                  <Icon name={item.icon} className="text-[18px]" aria-hidden />
                </span>
                <p className="font-body-md text-body-md lg:text-body-lg text-on-surface-variant leading-relaxed">
                  {item.label}
                </p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      {/* ===== HECHA POR COLECCIONISTAS ===== */}
      <section className="py-space-xl">
        <Container>
          <div className="rounded-xl border border-border-subtle bg-surface-deck px-margin-mobile lg:px-margin py-space-xl flex flex-col items-center text-center gap-space-md">
            <span className="w-12 h-12 rounded-full bg-vacuum-glow/10 border border-vacuum-glow/30 text-vacuum-glow flex items-center justify-center">
              <Icon name="favorite" className="text-[24px]" />
            </span>
            <h2 className="font-headline-lg text-headline-lg-mobile lg:text-headline-lg text-on-surface font-bold tracking-tight max-w-3xl text-balance">
              {t('marketing.home.madeTitle')}
            </h2>
            <p className="font-body-md text-body-md lg:text-body-lg text-on-surface-variant max-w-2xl leading-relaxed">
              {t('marketing.home.madeBody')}
            </p>
          </div>
        </Container>
      </section>

      {/* ===== PROPUESTA DE VALOR / FUNCIONES DESTACADAS ===== */}
      <section className="py-space-xl">
        <Container className="flex flex-col gap-space-xl">
          <SectionHeading
            eyebrow={t('marketing.home.valueEyebrow')}
            eyebrowIcon="stars"
            title={t('marketing.home.valueTitle')}
            subtitle={t('marketing.home.valueSubtitle')}
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-space-md">
            <FeatureCard icon="photo_camera" title={t('marketing.home.f1Title')}>
              {t('marketing.home.f1Body')}
            </FeatureCard>
            <FeatureCard icon="auto_awesome" title={t('marketing.home.f2Title')} accent="amber">
              {t('marketing.home.f2Body')}
            </FeatureCard>
            <FeatureCard icon="content_copy" title={t('marketing.home.f3Title')}>
              {t('marketing.home.f3Body')}
            </FeatureCard>
            <FeatureCard icon="query_stats" title={t('marketing.home.f4Title')} accent="amber">
              {t('marketing.home.f4Body')}
            </FeatureCard>
            <FeatureCard icon="bookmark" title={t('marketing.home.f5Title')}>
              {t('marketing.home.f5Body')}
            </FeatureCard>
            <FeatureCard icon="notifications_active" title={t('marketing.home.f6Title')} accent="amber">
              {t('marketing.home.f6Body')}
            </FeatureCard>
          </div>
          <div className="flex justify-center">
            <CtaSecondary to="/funciones" icon="arrow_forward">
              {t('marketing.home.viewAllFeatures')}
            </CtaSecondary>
          </div>
        </Container>
      </section>

      {/* ===== CÓMO FUNCIONA ===== */}
      <section className="py-space-xl bg-surface-deck border-y border-border-subtle">
        <Container className="grid lg:grid-cols-2 gap-space-xl items-center">
          <div className="order-2 lg:order-1 rounded-xl overflow-hidden border border-border-subtle">
            <img src={IMG.turntable} alt={t('marketing.home.howImageAlt')} className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="order-1 lg:order-2 flex flex-col gap-space-lg">
            <SectionHeading
              align="left"
              eyebrow={t('marketing.home.howEyebrow')}
              eyebrowIcon="bolt"
              title={t('marketing.home.howTitle')}
            />
            <ol className="flex flex-col gap-space-md">
              {[
                { n: '1', t: t('marketing.home.step1Title'), d: t('marketing.home.step1Body') },
                { n: '2', t: t('marketing.home.step2Title'), d: t('marketing.home.step2Body') },
                { n: '3', t: t('marketing.home.step3Title'), d: t('marketing.home.step3Body') },
              ].map((step) => (
                <li key={step.n} className="flex items-start gap-space-md">
                  <span className="w-10 h-10 shrink-0 rounded-full bg-primary-container/15 border border-primary-container/40 text-primary font-headline-sm font-bold flex items-center justify-center">
                    {step.n}
                  </span>
                  <div className="flex flex-col gap-space-xs">
                    <span className="font-headline-sm text-headline-md text-on-surface font-bold">{step.t}</span>
                    <span className="font-body-sm text-body-sm text-on-surface-variant leading-relaxed">{step.d}</span>
                  </div>
                </li>
              ))}
            </ol>
            <CtaPrimary to="/registro" className="self-start">
              {t('marketing.home.tryFree')}
            </CtaPrimary>
          </div>
        </Container>
      </section>

      {/* ===== PLANES (teaser) ===== */}
      <section className="py-space-xl">
        <Container className="flex flex-col gap-space-xl">
          <SectionHeading
            eyebrow={t('marketing.home.plansEyebrow')}
            eyebrowIcon="workspace_premium"
            title={t('marketing.home.plansTitle')}
            subtitle={t('marketing.home.plansSubtitle')}
          />
          <div className="flex flex-col sm:flex-row items-center justify-center gap-space-sm">
            <CtaPrimary to="/precios">{t('marketing.home.viewPlansPrices')}</CtaPrimary>
            <Link
              to="/registro"
              className="font-body-sm text-body-sm text-on-surface-variant hover:text-primary transition-colors inline-flex items-center gap-space-xs"
            >
              <Icon name="arrow_forward" className="text-[16px]" /> {t('marketing.home.orCreateFree')}
            </Link>
          </div>
        </Container>
      </section>

      {/* ===== BANDA VISUAL ===== */}
      <section className="py-space-xl">
        <Container>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-space-sm">
            <div className="rounded-xl overflow-hidden border border-border-subtle aspect-[4/5] md:aspect-square">
              <img src={IMG.coloredStack} alt={t('marketing.home.galleryStackAlt')} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="rounded-xl overflow-hidden border border-border-subtle aspect-[4/5] md:aspect-square">
              <img src={IMG.spinning} alt={t('marketing.home.gallerySpinningAlt')} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="col-span-2 md:col-span-1 rounded-xl overflow-hidden border border-border-subtle aspect-[16/9] md:aspect-square">
              <img src={IMG.recordsHeadphones} alt={t('marketing.home.galleryHeadphonesAlt')} className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </Container>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-space-xl bg-surface-deck border-y border-border-subtle">
        <Container className="flex flex-col gap-space-lg max-w-3xl">
          <SectionHeading eyebrow={t('marketing.home.faqEyebrow')} eyebrowIcon="help" title={t('marketing.home.faqTitle')} />
          <div className="flex flex-col gap-space-sm">
            {homeFaqs.map((f) => (
              <FaqItem key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </Container>
      </section>

      {/* ===== CTA DE CIERRE ===== */}
      <CtaBanner
        title={t('marketing.home.closingTitle')}
        subtitle={t('marketing.home.closingSubtitle')}
      />
    </>
  );
}
